import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { KeyOutlined, ArrowLeftOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { message, Form, Input, Button, Card } from 'antd';

const ChangePassword = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { changePassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (values) => {
    setLoading(true);

    // Validation
    if (values.newPassword !== values.confirmPassword) {
      message.error('New password and confirm password do not match');
      setLoading(false);
      return;
    }

    if (values.newPassword.length < 6) {
      message.error('New password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      const result = await changePassword(values.currentPassword, values.newPassword);

      if (result.success) {
        message.success('Password changed successfully!');
        // Clear form
        form.resetFields();
      }
    } catch (error) {
      message.error('Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="site-card-border-less-wrapper" style={{ padding: '24px' }}>
      <div style={{ maxWidth: '500px', margin: '0 auto' }}>
        <div style={{ marginBottom: '24px' }}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/agent-dashboard')}
            style={{ padding: '4px 8px' }}
          >
            Back to Dashboard
          </Button>
        </div>

        <Card
          title={
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                height: '48px', 
                width: '48px', 
                backgroundColor: '#1890ff', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <KeyOutlined style={{ fontSize: '24px', color: '#fff' }} />
              </div>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
                Change Password
              </h2>
              <p style={{ margin: '8px 0 0', color: '#666', fontSize: '14px' }}>
                Enter your current password and choose a new one
              </p>
            </div>
          }
          style={{ 
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px'
          }}
        >
          <Form
            form={form}
            onFinish={handleSubmit}
            layout="vertical"
            style={{ marginTop: '24px' }}
          >
            <Form.Item
              label="Current Password"
              name="currentPassword"
              rules={[
                { required: true, message: 'Please enter your current password' }
              ]}
            >
              <Input.Password
                placeholder="Enter current password"
                iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                size="large"
              />
            </Form.Item>

            <Form.Item
              label="New Password"
              name="newPassword"
              rules={[
                { required: true, message: 'Please enter your new password' },
                { min: 6, message: 'Password must be at least 6 characters long' }
              ]}
            >
              <Input.Password
                placeholder="Enter new password"
                iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                size="large"
              />
            </Form.Item>

            <Form.Item
              label="Confirm New Password"
              name="confirmPassword"
              rules={[
                { required: true, message: 'Please confirm your new password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('The two passwords do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password
                placeholder="Confirm new password"
                iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                size="large"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                block
                style={{
                  height: '44px',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default ChangePassword;
